import React, { useEffect, useState } from 'react';

import Icon from 'views/site/Icon.js';

import Button from 'views/site/Button.js';
import { getSpecs } from 'files/ProductSpecs.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * ProductDetail
 *
 * Ported from the storefront. The buy panel becomes a quote panel: no price,
 * no compare-at, no instalments, no volume pricing note, no serviceability
 * gate. What replaces the price is the product's own feature list, which the
 * storefront had no room for because the price was occupying that space.
 *
 * The "where it goes" section is new. It answers the question a homeowner
 * actually has once they are interested, which the storefront never had to
 * address because it shipped a box rather than installing a network. The
 * content is transcribed from each manual's placement pages into
 * files/ProductSpecs.js rather than shown as a photograph of the page.
 */

const ProductDetail = ({ onAddToQuote, onBack, product }) => {

    const mobile = Utils.isMobile();
    const [index, setIndex] = useState(0);

    const specs = product ? getSpecs(product.handle) : null;
    const images = product?.images || [];

    // Reset the gallery whenever the product changes
    useEffect(() => {
        window.scrollTo(0, 0);
        setIndex(0);
    }, [product?.handle]);

    // Auto-advance where there is more than one image
    useEffect(() => {
        if(images.length <= 1) {
            return;
        }
        let timer = setInterval(() => {
            setIndex(current => (current + 1) % images.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [images.length, product?.handle]);

    if(!product) {
        return (
            <section style={{
                margin: '0 auto',
                maxWidth: 680,
                padding: '160px 20px',
                textAlign: 'center'
            }}>
                <span style={{
                    ...Theme.textStyles.heading(),
                    display: 'block',
                    marginBottom: 24
                }}>{'Product not found'}</span>
                <Button
                label={'All products'}
                onClick={onBack}
                type={'primary'} />
            </section>
        )
    }

    const getGallery = () => {
        return (
            <div>
                <div style={{
                    backgroundColor: Theme.colors.offWhite,
                    borderRadius: 28,
                    height: mobile ? 320 : 480,
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    {images.length > 0 ? images.map((image, imageIndex) => (
                        <img
                        alt={image.altText || product.title}
                        key={imageIndex}
                        src={image.src}
                        style={{
                            height: '100%',
                            left: 0,
                            objectFit: 'cover',
                            opacity: imageIndex === index ? 1 : 0,
                            position: 'absolute',
                            top: 0,
                            transition: 'opacity 0.6s ease',
                            width: '100%'
                        }} />
                    )) : (
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            height: '100%',
                            justifyContent: 'center',
                            width: '100%'
                        }}>
                            <Icon
                            color={Theme.colors.lightGrey}
                            name={product.icon || 'box-seam'}
                            size={110} />
                        </div>
                    )}
                </div>

                {images.length > 1 && (
                    <div style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'center',
                        marginTop: 18
                    }}>
                        {images.map((image, imageIndex) => (
                            <button
                            aria-label={`View image ${imageIndex + 1}`}
                            key={imageIndex}
                            onClick={setIndex.bind(this, imageIndex)}
                            style={{
                                backgroundColor: imageIndex === index ? Theme.colors.primary() : Theme.colors.border(),
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                height: 8,
                                padding: 0,
                                transition: 'all 0.3s ease',
                                width: imageIndex === index ? 22 : 8
                            }} />
                        ))}
                    </div>
                )}
            </div>
        )
    }

    const getQuotePanel = () => {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                <span style={{
                    ...Theme.textStyles.label(),
                    display: 'block',
                    marginBottom: 14
                }}>{specs?.tagline || product.subtitle}</span>

                <span style={{
                    ...Theme.textStyles.heading({ fontSize: mobile ? 32 : 46 }),
                    display: 'block',
                    marginBottom: 18
                }}>{product.title}</span>

                <span style={{
                    ...Theme.textStyles.body({ fontSize: mobile ? 16 : 18, lineHeight: 1.5 }),
                    display: 'block',
                    marginBottom: 28,
                    maxWidth: '44ch'
                }}>{product.description}</span>

                {/* Occupies the space the price used to hold */}
                {product.features?.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 11,
                        marginBottom: 32
                    }}>
                        {product.features.map((feature, featureIndex) => (
                            <span
                            key={featureIndex}
                            style={{
                                alignItems: 'flex-start',
                                display: 'flex',
                                gap: 11
                            }}>
                                <Icon name={'check-lg'} size={12} color={Theme.colors.primary()} style={{ flexShrink: 0, paddingTop: 5 }} />
                                <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15 })}>
                                    {feature}
                                </span>
                            </span>
                        ))}
                    </div>
                )}

                <div>
                    <Button
                    icon={'plus-lg'}
                    label={'Add to My Network'}
                    onClick={onAddToQuote.bind(this, product)}
                    style={{ fontSize: 16, padding: '15px 40px' }}
                    type={'primary'} />
                </div>

                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 22,
                    marginTop: 24
                }}>
                    {['Professionally installed', 'No monitoring fees', 'Part of one connected network'].map((item, itemIndex) => (
                        <span
                        key={itemIndex}
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            gap: 7
                        }}>
                            <Icon name={'check-lg'} size={12} color={Theme.colors.primary()} />
                            <span style={Theme.textStyles.caption({ color: Theme.colors.subText() })}>{item}</span>
                        </span>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div
        className={'site'}
        style={{
            paddingTop: Theme.layout.headerOffset(),
            width: '100%'
        }}>
            <section style={{ backgroundColor: Theme.colors.white }}>
                <div style={{
                    margin: '0 auto',
                    maxWidth: Theme.layout.maxWidth,
                    padding: mobile ? '28px 20px 64px' : '36px 40px 96px'
                }}>
                    <span
                    onClick={onBack}
                    style={{
                        color: Theme.colors.primary(),
                        cursor: 'pointer',
                        display: 'inline-block',
                        fontFamily: Theme.fonts.body,
                        fontSize: 15,
                        fontWeight: 500,
                        marginBottom: 28
                    }}>{'‹ All products'}</span>

                    <div style={{
                        alignItems: 'center',
                        display: 'grid',
                        gap: mobile ? 36 : 72,
                        gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
                    }}>
                        {getGallery()}
                        {getQuotePanel()}
                    </div>
                </div>
            </section>

            {specs?.highlights && (
                <section style={{ backgroundColor: Theme.colors.offWhite }}>
                    <div style={{
                        margin: '0 auto',
                        maxWidth: Theme.layout.maxWidth,
                        padding: Theme.layout.sectionPadding()
                    }}>
                        <div style={{
                            display: 'grid',
                            gap: mobile ? 20 : 24,
                            gridTemplateColumns: mobile ? '1fr' : 'repeat(3, 1fr)'
                        }}>
                            {specs.highlights.map((highlight, highlightIndex) => (
                                <div
                                key={highlightIndex}
                                style={{
                                    ...Theme.styles.card(),
                                    boxShadow: Theme.boxShadow.card,
                                    padding: mobile ? 24 : 28
                                }}>
                                    <div style={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        gap: 12,
                                        marginBottom: 10
                                    }}>
                                        <Icon name={highlight.icon} size={19} color={Theme.colors.primary()} style={{ flexShrink: 0 }} />
                                        <span style={Theme.textStyles.subHeading({ fontSize: 18 })}>{highlight.title}</span>
                                    </div>
                                    <span style={Theme.textStyles.body({ fontSize: 15 })}>{highlight.copy}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Where it goes. Only the fire and CO sensors have a diagram. */}
            {/*
              * Where it goes.
              *
              * This was a photograph of the manual's placement page. A screenshot
              * of a document cannot be searched, translated, read aloud, or made
              * to reflow on a phone, and it looked like a scan dropped into the
              * page. The same information is transcribed into ProductSpecs and
              * rendered here — the rooms it belongs in on one side, the rooms it
              * must not go in on the other, each with the reason, because the
              * reason is the part that actually helps someone.
              */}
            {specs?.placement && (
                <section style={{ backgroundColor: Theme.colors.white }}>
                    <div style={{
                        margin: '0 auto',
                        maxWidth: Theme.layout.readableWidth,
                        padding: Theme.layout.sectionPadding()
                    }}>
                        <span style={{
                            ...Theme.textStyles.sectionHeading({ textAlign: 'left' }),
                            display: 'block',
                            marginBottom: 10
                        }}>{'Where it goes'}</span>
                        <span style={{
                            ...Theme.textStyles.body(),
                            display: 'block',
                            marginBottom: mobile ? 28 : 36
                        }}>{`Your installer works the exact positions out with you during the walkthrough. This is the guidance they start from, straight out of the ${product.shortTitle} manual.`}</span>

                        <div style={{
                            display: 'grid',
                            gap: mobile ? 20 : 24,
                            gridTemplateColumns: mobile ? '1fr' : '1fr 1fr'
                        }}>
                            {[
                                { items: specs.placement.where, positive: true, title: 'Belongs here' },
                                { items: specs.placement.avoid, positive: false, title: 'Not in these rooms' }
                            ].map((column, columnIndex) => (
                                <div
                                key={columnIndex}
                                style={{
                                    ...Theme.styles.card(),
                                    boxShadow: Theme.boxShadow.card,
                                    padding: mobile ? 20 : 24
                                }}>
                                    <div style={{
                                        alignItems: 'center',
                                        borderBottom: `1px solid ${Theme.colors.divider()}`,
                                        display: 'flex',
                                        gap: 10,
                                        marginBottom: 16,
                                        paddingBottom: 14
                                    }}>
                                        <Icon
                                        color={column.positive ? Theme.colors.primary() : Theme.colors.lightGrey}
                                        name={column.positive ? 'check-lg' : 'x-lg'}
                                        size={16} />
                                        <span style={Theme.textStyles.subHeading({ fontSize: 17 })}>{column.title}</span>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 16
                                    }}>
                                        {column.items.map((item, itemIndex) => (
                                            <div key={itemIndex}>
                                                <div style={{
                                                    alignItems: 'baseline',
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 8,
                                                    marginBottom: 3
                                                }}>
                                                    <span style={Theme.textStyles.body({
                                                        color: Theme.colors.text(),
                                                        fontSize: 15,
                                                        fontWeight: 600
                                                    })}>{item.where}</span>

                                                    {/* the heat sensor is set per room, and the
                                                        setting is the whole point of the room */}
                                                    {item.setting && (
                                                        <span style={{
                                                            backgroundColor: Theme.tint(0.1),
                                                            borderRadius: 999,
                                                            color: Theme.colors.primary(),
                                                            fontFamily: Theme.fonts.body,
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            padding: '2px 8px',
                                                            whiteSpace: 'nowrap'
                                                        }}>{item.setting}</span>
                                                    )}

                                                    {item.required === true && (
                                                        <span style={{
                                                            ...Theme.textStyles.caption({ color: Theme.colors.subText(), fontSize: 11 }),
                                                            letterSpacing: '0.06em',
                                                            textTransform: 'uppercase'
                                                        }}>{'Required'}</span>
                                                    )}
                                                </div>
                                                <span style={Theme.textStyles.body({ fontSize: 14 })}>{item.note}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {specs?.specs && (
                <section style={{ backgroundColor: Theme.colors.offWhite }}>
                    <div style={{
                        margin: '0 auto',
                        maxWidth: Theme.layout.readableWidth,
                        padding: Theme.layout.sectionPadding()
                    }}>
                        <span style={{
                            ...Theme.textStyles.sectionHeading({ textAlign: 'left' }),
                            display: 'block',
                            marginBottom: 28
                        }}>{'Tech specs'}</span>
                        <div style={{
                            backgroundColor: Theme.colors.white,
                            border: `1px solid ${Theme.colors.border()}`,
                            borderRadius: 16,
                            overflow: 'hidden'
                        }}>
                            {specs.specs.map((row, rowIndex) => (
                                <div
                                key={rowIndex}
                                style={{
                                    borderBottom: rowIndex < specs.specs.length - 1 ? `1px solid ${Theme.colors.divider()}` : 'none',
                                    display: 'flex',
                                    gap: 20,
                                    padding: mobile ? '14px 16px' : '16px 22px'
                                }}>
                                    <span style={{
                                        ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: 14 }),
                                        flexShrink: 0,
                                        width: '38%'
                                    }}>{row.label}</span>
                                    <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15, fontWeight: 500 })}>
                                        {row.value}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {specs.inTheBox && (
                            <div style={{ marginTop: 40 }}>
                                <span style={{
                                    ...Theme.textStyles.subHeading(),
                                    display: 'block',
                                    marginBottom: 16
                                }}>{'In the box'}</span>
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 12
                                }}>
                                    {specs.inTheBox.map((item, itemIndex) => (
                                        <span
                                        key={itemIndex}
                                        style={{
                                            alignItems: 'center',
                                            backgroundColor: Theme.colors.white,
                                            border: `1px solid ${Theme.colors.border()}`,
                                            borderRadius: 999,
                                            display: 'inline-flex',
                                            gap: 8,
                                            padding: '10px 16px'
                                        }}>
                                            <Icon name={'check-lg'} size={11} color={Theme.colors.primary()} />
                                            <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14 })}>{item}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            <section style={{ backgroundColor: Theme.colors.white }}>
                <div style={{
                    margin: '0 auto',
                    maxWidth: Theme.layout.maxWidth,
                    padding: Theme.layout.sectionPadding(),
                    textAlign: 'center'
                }}>
                    <span style={{
                        ...Theme.textStyles.sectionHeading(),
                        display: 'block',
                        marginBottom: 14
                    }}>{`Add the ${product.shortTitle} to your network`}</span>
                    <span style={{
                        ...Theme.textStyles.sectionSubheading(),
                        display: 'block',
                        margin: '0 auto 28px'
                    }}>{'Build a list of what you want, and your local dealer will price the network after seeing your home. No obligation.'}</span>
                    <Button
                    icon={'plus-lg'}
                    label={'Add to My Network'}
                    onClick={onAddToQuote.bind(this, product)}
                    style={{ fontSize: 16, padding: '15px 40px' }}
                    type={'primary'} />
                </div>
            </section>
        </div>
    )
}

export default ProductDetail;
